import { getSupabaseBrowserClient } from './supabase';
import { authDiagnosticStamp, captureV3AuthDiagnostic, captureV3ReadOutcome } from './generation-v3-auth-diagnostics';
import { V3_DIAGNOSTIC_HEADER, v3DiagnosticHeader } from './generation-v3-diagnostic-header';

export type Location = { source_id: string; kind: 'page' | 'slide'; number: number; label: string };
export type Content = { type: 'basic'; front: string; back: string; tags: string[] } |
  { type: 'cloze'; cloze_text: string; tags: string[] } |
  { type: 'image_occlusion'; front: string; answer: string; hint: string | null; tags: string[]; image?: { url: string }; occluded_image?: { url: string } };
export type Card = { id: string; ordinal: number; revision: number; decision: 'included' | 'rejected'; edited: boolean; source: Location | null; content: Content };
export type Generation = {
  id: string; title: string; status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
  card_limit?: number | null;
  counts: { generated: number; included: number; rejected: number }; review_revision: number;
  progress: { generated_cards: number; total_cards: number | null; total_is_final: boolean };
  phases: Record<string, { status: string }>; poll_after_ms: number | null;
  finalization: null | { status: 'finishing' | 'ready' | 'failed'; error: { code: string; retryable?: boolean } | null };
  library_deck_id?: string | null;
  time_estimate: { min_seconds: number; max_seconds: number; confidence?: 'low' | 'medium' | 'high';
    calculated_at?: string; source?: 'history' | 'history_with_priors'; worker_capacity_known?: boolean } | null;
  error: { code: string; retryable?: boolean; message?: string } | null;
  image_progress?: { processed_images: number; total_images: number } | null;
  timing?: { created_at: string; first_card_ready_at: string | null; first_review_page_ready_at: string | null; source_slide_count: number };
};
export type CardPage = { cards: Card[]; next_cursor: string | null; has_more: boolean; generation_complete: boolean };
export type Source = { source: { location: Location; text: string }; adjacent: { location: Location; text: string }[] };
export type Export = { export_id: string; status: string; download: null | { url: string; filename: string }; error: { code: string; retryable?: boolean } | null };
export class V3Error extends Error {
  constructor(public code: string, message: string, public status?: number) { super(message); }
}

export function generationFailureHeading(error: Generation['error'] | undefined): string {
  return error?.code === 'lecture_input_limit' ? 'Document too large' : 'Generation needs another try';
}

export function generationFailureMessage(error: Generation['error'] | undefined): string {
  if (error?.code === 'lecture_input_limit') return 'This document contains too much text to process at once. Split it into smaller files, then upload each file separately.';
  // Only invalid_pptx has backend-owned, safe user-facing copy. Other errors stay generic.
  if (error?.code === 'invalid_pptx' && typeof error.message === 'string' && error.message.trim()) return error.message;
  return 'Your saved cards, edits and rejections are preserved.';
}

function deviceId() {
  const key = 'slidetoanki-v3-device';
  let value = localStorage.getItem(key);
  if (!value) { value = crypto.randomUUID(); localStorage.setItem(key, value); }
  return value;
}
type BrowserClient = NonNullable<ReturnType<typeof getSupabaseBrowserClient>>;
const sessionRefreshes = new WeakMap<BrowserClient, ReturnType<BrowserClient['auth']['refreshSession']>>();
function refreshReadSession(auth: BrowserClient) {
  let pending = sessionRefreshes.get(auth);
  if (!pending) {
    pending = auth.auth.refreshSession().finally(() => sessionRefreshes.delete(auth));
    sessionRefreshes.set(auth, pending);
  }
  return pending;
}

export async function v3<T>(path: string, body?: unknown, method = body === undefined ? 'GET' : 'POST', key = crypto.randomUUID(), signal?: AbortSignal, retryTransport = true): Promise<T> {
  // Correlate only this logical GET, including its existing retries. Never use a
  // deck, user, cursor or token identifier; ordinary successful reads stay quiet.
  const read = method === 'GET' ? { id: crypto.randomUUID(), attempts: 0, status: null as number | null, failed: false, unauthorized: false } : undefined;
  try {
    const result = await v3Request<T>(path, body, method, key, signal, retryTransport, read);
    if (read?.failed) captureV3ReadOutcome(read.id, 'recovered', read);
    return result;
  } catch (error) {
    if (read) captureV3ReadOutcome(read.id, signal?.aborted ? 'cancelled' : 'terminal', read);
    throw error;
  }
}

type ReadObservation = { id: string; attempts: number; status: number | null; failed: boolean; unauthorized: boolean };
async function v3Request<T>(path: string, body: unknown, method: string, key: string, signal: AbortSignal | undefined, retryTransport: boolean, read?: ReadObservation): Promise<T> {
  const auth = getSupabaseBrowserClient();
  const headers: Record<string, string> = { 'X-Device-ID': deviceId() };
  if (method !== 'GET') { headers['Idempotency-Key'] = key; headers['Content-Type'] = 'application/json'; }
  const serializedBody = body === undefined ? undefined : JSON.stringify(body);
  let authRetried = false;
  let requestUserId: string | undefined;
  let hasSession = false;
  // Reuse the same idempotency key and body: an unknown response may have committed.
  for (let attempt = 0; ; ) {
    signal?.throwIfAborted();
    const lookupStarted = authDiagnosticStamp();
    const session = auth && await auth.auth.getSession();
    signal?.throwIfAborted();
    if (!session?.data.session?.access_token || session.error) throw new V3Error('authentication_required', 'Sign in to continue.');
    const userId = session.data.session.user?.id;
    if (hasSession && userId !== requestUserId) throw new V3Error('authentication_required', 'Your account changed. Please try again.');
    requestUserId = userId; hasSession = true;
    const diagnostic = { method, operationId: read?.id, lookupStarted, lookupCompleted: authDiagnosticStamp() };
    const diagnosticSession = { access_token: session.data.session.access_token, expires_at: session.data.session.expires_at };
    headers.Authorization = `Bearer ${session.data.session.access_token}`;
    try {
      const timeout = AbortSignal.timeout(method === 'GET' ? 15_000 : 60_000);
      const sent = authDiagnosticStamp();
      const diagnosticHeader = v3DiagnosticHeader(lookupStarted, diagnostic.lookupCompleted, sent, attempt, authRetried);
      if (diagnosticHeader) headers[V3_DIAGNOSTIC_HEADER] = diagnosticHeader;
      else delete headers[V3_DIAGNOSTIC_HEADER];
      if (read) read.attempts++;
      const response = await fetch('/api/v3' + path, { method, headers, signal: signal ? AbortSignal.any([signal, timeout]) : timeout, cache: 'no-store', ...(serializedBody === undefined ? {} : { body: serializedBody }) });
      if (read) {
        read.status = response.status;
        if (!response.ok) read.failed = true;
        if (response.status === 401) read.unauthorized = true;
      }
      if (response.status === 401 || authRetried) captureV3AuthDiagnostic(response.status === 401 ? 'unauthorized' : 'retry_response', diagnosticSession, {
        ...diagnostic, sent, received: authDiagnosticStamp(), status: response.status, transportAttempt: attempt, authRetried, responseDate: response.headers.get('Date'),
      });
      // Recover expired read sessions once without adding mutation replays.
      if (response.status === 401 && method === 'GET' && !authRetried && auth) {
        authRetried = true;
        signal?.throwIfAborted();
        const latest = await auth.auth.getSession().catch(() => null);
        signal?.throwIfAborted();
        // Another request/tab may already have refreshed. Never recover across accounts.
        if (!latest?.error && latest?.data.session?.access_token && latest.data.session.user?.id === requestUserId) {
          if (latest.data.session.access_token !== diagnosticSession.access_token) continue;
          const refreshStarted = authDiagnosticStamp();
          const refreshed = await refreshReadSession(auth).catch(() => null);
          captureV3AuthDiagnostic('refresh_completed', refreshed?.data.session, { ...diagnostic, refreshStarted, refreshCompleted: authDiagnosticStamp(),
            refreshOutcome: !refreshed || refreshed.error ? 'failed' : refreshed.data.session?.access_token ? 'succeeded' : 'missing_session',
            tokenChanged: !!refreshed?.data.session?.access_token && headers.Authorization !== `Bearer ${refreshed.data.session.access_token}`,
          });
          signal?.throwIfAborted();
          if (!refreshed?.error && refreshed?.data.session?.access_token && refreshed.data.session.user?.id === requestUserId) continue;
        }
      }
      const data = await response.json();
      if (!response.ok) throw new V3Error(data.error?.code || 'request_failed', data.error?.message || 'Request failed.', response.status);
      return data as T;
    } catch (error) {
      if (read) read.failed = true;
      if (error instanceof V3Error || signal?.aborted || !retryTransport || attempt >= 1) throw error;
      attempt++;
    }
  }
}

export async function startV3(file: File, onStage: (stage: string) => void, options: {
  depth: 'concise' | 'balanced' | 'thorough';
  cardTypes: ('cloze' | 'basic' | 'image_occlusion')[];
}, signal?: AbortSignal): Promise<Generation> {
  const mime: Record<string, string> = { pdf: 'application/pdf', ppt: 'application/vnd.ms-powerpoint', pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' };
  const type = mime[file.name.split('.').pop()?.toLowerCase() || ''];
  if (!type || file.size <= 0 || file.size > 50 * 1024 * 1024) throw new Error('Choose a PDF or PowerPoint file up to 50 MB.');
  signal?.throwIfAborted();
  onStage('Preparing your upload…');
  const bytes = await file.arrayBuffer();
  signal?.throwIfAborted();
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  signal?.throwIfAborted();
  const sha256 = [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
  const upload = await v3<{ upload_id: string; upload: { url: string; method: string; headers: Record<string, string> } }>('/document-uploads', { filename: file.name, mime_type: type, size_bytes: file.size, sha256 }, 'POST', undefined, signal);
  signal?.throwIfAborted();
  onStage('Uploading your document…');
  const form = new FormData(); form.append('file', new Blob([file], { type }), file.name);
  const response = await fetch(upload.upload.url, { method: upload.upload.method, headers: upload.upload.headers, body: form, signal });
  signal?.throwIfAborted();
  if (!response.ok) throw new Error('The document upload failed. Please try again.');
  onStage('Starting your deck…');
  const generation = await v3<Generation>('/deck-generations', { upload_id: upload.upload_id, depth: options.depth, card_types: options.cardTypes, title: file.name.replace(/\.[^.]+$/, '') }, 'POST', undefined, signal);
  signal?.throwIfAborted();
  return generation;
}
